import { api } from './api'
import { getGuessedTimezone } from 'utils/time'
import { fetchTicketingSystems } from 'api/ticketingSystems'
import * as sessionUserSelector from 'selectors/sessionUser'

const enhancedApi = api.enhanceEndpoints({
  addTagTypes: [
    'sessionUser',
  ],
})

const extendedApi = enhancedApi.injectEndpoints({
  endpoints: (builder) => ({
    restoreSession: builder.query({
      query: () => '/sessions/restore',
      transformResponse: (response, meta, arg) => {
        let message = {}
        if (arg?.isSessionTimeout) {
          message = {
            message: {
              text: 'Session timeout due to inactivity',
              isPersistent: true,
              type: 'success',
            },
          }
        }
        return { ...response, ...message }
      },
    }),

    authUser: builder.mutation({
      query: ({ token, partnerId }) => ({
        url: `/partner_sessions?code=${token}&partner_id=${partnerId}`,
        method: 'POST',
      }),
    }),

    signOutUser: builder.mutation({
      query: () => ({
        url: '/sessions',
        method: 'DELETE',
      }),
      transformResponse: (response, meta, arg) => {
        let message = {}
        if (arg?.isSessionTimeout) {
          message = {
            message: {
              text: 'Session timeout due to inactivity',
              isPersistent: true,
              type: 'success',
              status: response?.message?.status,
            },
          }
        }
        return { ...response, ...message }
      },
    }),

    updatePartnerView: builder.mutation({
      query: (partnerId) => ({
        url: '/admin/change_partner_view',
        method: 'PATCH',
        body: { partnerId: partnerId },
      }),
    }),

    fetchSessionUser: builder.query({
      query: () => '/users/show',
      providesTags: (result) => ['sessionUser'],
    }),

    // Updated user data will be taken from the response of this API request in sessionUser reducer
    // so we don't need to invalidate tags here and make a new request.
    updateSessionUser: builder.mutation({
      query: (params) => {
        if (params.timezoneAutodetect) {
          params.timezone = getGuessedTimezone()
        }
        return ({
          url: '/users/update',
          method: 'PATCH',
          body: params,
          meta: params,
        })
      },
      // Refetch ticketing systems when locale is changed
      async onQueryStarted (arg, { queryFulfilled, dispatch, getState }) {
        const localeBefore = sessionUserSelector.locale(getState())

        await queryFulfilled
        const localeAfter = sessionUserSelector.locale(getState())
        if (localeBefore !== localeAfter) {
          // without "forceRefetch" the request did not start and there was an error in Redux log (not in console):
          // "Aborted due to condition callback returning false."
          dispatch(fetchTicketingSystems.initiate(undefined, { forceRefetch: true }))
        }
      },
    }),

    // single param update
    updateSessionUserParam: builder.mutation({
      query: (params) => {
        return ({
          url: '/users/update',
          method: 'PATCH',
          body: params,
        })
      },
    }),

    updateUserAvatar: builder.mutation({
      query: ({ avatar }) => {
        return ({
          url: '/update_avatar',
          method: 'POST',
          body: avatar || {},
        })
      },
      extraOptions: { isFileUpload: true },
    }),

    // sandbox sign in
    sandboxSignIn: builder.mutation({
      query: ({ login, partnerId, role }) => ({
        url: '/partner_sessions',
        method: 'POST',
        params: {
          login: login,
          partnerId: partnerId,
          role: role,
          sandboxAuth: true,
        },
      }),
    }),

    sandboxAuthenticate: builder.query({
      query: () => ({
        url: '/sessions/sandbox_auth',
      }),
    }),
  }),
  overrideExisting: false,
})

export const {
  useUpdatePartnerViewMutation,
  useFetchSessionUserQuery,
  useSignOutUserMutation,
  useUpdateSessionUserMutation,
  useUpdateSessionUserParamMutation,
  useUpdateUserAvatarMutation,
  useSandboxSignInMutation,
  useSandboxAuthenticateQuery,
} = extendedApi

export const {
  restoreSession,
  authUser,
  signOutUser,
  updatePartnerView,
  fetchSessionUser,
  updateSessionUser,
  updateSessionUserParam,
  updateUserAvatar,
  sandboxSignIn,
} = extendedApi.endpoints
